<template>
  <v-dialog v-model="dialog" max-width="560px" persistent>
    <v-card class="review-card rounded-xl pa-0">
      <!-- Header -->
      <div class="review-header px-6 pt-5 pb-4">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center" style="gap:10px">
            <div class="review-icon-wrap">
              <v-icon color="white" size="20">mdi-star-outline</v-icon>
            </div>
            <div>
              <div class="review-title">รีวิวคำสั่งซื้อ</div>
              <div class="review-sub">{{ order ? (order.seller?.shopName || 'ร้านค้า') : '' }}</div>
            </div>
          </div>
          <v-btn icon small @click="close"><v-icon color="#94a3b8">mdi-close</v-icon></v-btn>
        </div>

        <!-- Tab switcher -->
        <div class="review-tabs mt-4">
          <button
            v-for="tab in availableTabs"
            :key="tab.key"
            :class="['rtab', { 'rtab--active': activeTab === tab.key, 'rtab--done': tab.done }]"
            @click="activeTab = tab.key"
          >
            <v-icon size="14" class="mr-1">{{ tab.done ? 'mdi-check-circle' : tab.icon }}</v-icon>
            {{ tab.label }}
          </button>
        </div>
      </div>

      <v-divider />

      <!-- Tab: ร้านค้า -->
      <div v-if="activeTab === 'seller'" class="review-body px-6 py-5">
        <div class="section-label">ให้คะแนนร้านค้า</div>
        <div class="shop-info d-flex align-center mb-4" style="gap:10px">
          <v-avatar size="44" color="#f1f5f9">
            <v-img v-if="order.seller?.shopImage" :src="order.seller.shopImage" />
            <v-icon v-else color="#94a3b8" size="24">mdi-store-outline</v-icon>
          </v-avatar>
          <div>
            <div class="info-name">{{ order.seller?.shopName || 'ร้านค้า' }}</div>
            <div class="info-sub">บริการ / ความตรงต่อเวลา / ความพร้อมสินค้า</div>
          </div>
        </div>
        <div class="d-flex mb-3" style="gap:4px">
          <v-btn v-for="n in 5" :key="n" icon small
            @mouseenter="sellerHover = n" @mouseleave="sellerHover = 0" @click="sellerRating = n">
            <v-icon :color="(sellerHover || sellerRating) >= n ? '#F59E0B' : '#d1d5db'" size="30">
              {{ (sellerHover || sellerRating) >= n ? 'mdi-star' : 'mdi-star-outline' }}
            </v-icon>
          </v-btn>
          <span v-if="sellerRating" class="ml-2 caption font-weight-bold align-self-center" style="color:#B45309;">{{ sellerRating }}/5</span>
        </div>
        <v-textarea v-model="sellerComment" placeholder="แสดงความคิดเห็นเพิ่มเติม (ไม่บังคับ)" outlined dense auto-grow
          rows="2" hide-details="auto" class="rounded-lg" background-color="#f8fafc" />
      </div>

      <!-- Tab: ไรเดอร์ -->
      <div v-else-if="activeTab === 'rider'" class="review-body px-6 py-5">
        <div class="section-label">ให้คะแนนไรเดอร์</div>
        <div class="shop-info d-flex align-center mb-4" style="gap:10px">
          <v-avatar size="44" color="#f1f5f9">
            <v-img v-if="order.rider?.profileImage" :src="order.rider.profileImage" />
            <v-icon v-else color="#94a3b8" size="24">mdi-account-outline</v-icon>
          </v-avatar>
          <div>
            <div class="info-name">{{ riderName }}</div>
            <div class="info-sub">ความเร็ว / ความสุภาพ / ความระมัดระวัง</div>
          </div>
        </div>
        <div class="d-flex mb-3" style="gap:4px">
          <v-btn v-for="n in 5" :key="n" icon small
            @mouseenter="riderHover = n" @mouseleave="riderHover = 0" @click="riderRating = n">
            <v-icon :color="(riderHover || riderRating) >= n ? '#F59E0B' : '#d1d5db'" size="30">
              {{ (riderHover || riderRating) >= n ? 'mdi-star' : 'mdi-star-outline' }}
            </v-icon>
          </v-btn>
          <span v-if="riderRating" class="ml-2 caption font-weight-bold align-self-center" style="color:#B45309;">{{ riderRating }}/5</span>
        </div>
        <v-textarea v-model="riderComment" placeholder="แสดงความคิดเห็นเพิ่มเติม (ไม่บังคับ)" outlined dense auto-grow
          rows="2" hide-details="auto" class="rounded-lg" background-color="#f8fafc" />
      </div>

      <!-- Tab: สินค้า -->
      <div v-else-if="activeTab === 'products'" class="review-body px-6 py-5">
        <div class="section-label">ให้คะแนนสินค้า</div>
        <div v-for="item in reviewableItems" :key="item.id" class="product-review-row mb-5">
          <div class="d-flex align-center mb-2" style="gap:10px">
            <v-img
              :src="getProductImage(item.product)"
              width="48" height="48"
              class="rounded-lg flex-shrink-0"
              style="border:1px solid #f1f5f9"
            />
            <div class="product-review-name">{{ item.productName }}</div>
          </div>
          <div class="d-flex mb-2" style="gap:4px">
            <v-btn v-for="n in 5" :key="n" icon small
              @mouseenter="$set(productHovers, item.id, n)" @mouseleave="$set(productHovers, item.id, 0)"
              @click="$set(productRatings, item.id, n)">
              <v-icon :color="(productHovers[item.id] || productRatings[item.id] || 0) >= n ? '#F59E0B' : '#d1d5db'" size="28">
                {{ (productHovers[item.id] || productRatings[item.id] || 0) >= n ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
            </v-btn>
            <span v-if="productRatings[item.id]" class="ml-2 caption font-weight-bold align-self-center" style="color:#B45309;">{{ productRatings[item.id] }}/5</span>
          </div>
          <v-textarea
            v-model="productComments[item.id]"
            placeholder="ความคิดเห็นเกี่ยวกับสินค้า (ไม่บังคับ)"
            outlined dense auto-grow rows="2" hide-details="auto"
            class="rounded-lg" background-color="#f8fafc"
          />
          <v-divider v-if="reviewableItems.indexOf(item) < reviewableItems.length - 1" class="mt-4" />
        </div>
        <div v-if="reviewableItems.length === 0" class="text-center grey--text py-4">
          รีวิวสินค้าครบแล้ว
        </div>
      </div>

      <v-divider />

      <v-card-actions class="pa-4 d-flex justify-end" style="gap:8px">
        <v-btn text @click="close" :disabled="saving">ยกเลิก</v-btn>
        <v-btn
          color="#F4811F" dark class="rounded-lg font-weight-bold px-6" elevation="0"
          :loading="saving" :disabled="!canSubmit"
          @click="submit"
        >
          <v-icon left small>mdi-send-outline</v-icon>ส่งรีวิว
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getProductImage } from "@/utils/image";
import { createRiderReviewApi, createSellerReviewApi, createProductReviewApi } from "@/services/api/review";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "ReviewDialog",

  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, default: null },
  },

  data() {
    return {
      activeTab: "seller",
      sellerRating: 0,
      sellerComment: "",
      sellerHover: 0,
      riderRating: 0,
      riderComment: "",
      riderHover: 0,
      productRatings: {},
      productComments: {},
      productHovers: {},
      saving: false,
    };
  },

  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },

    riderName() {
      const r = this.order?.rider;
      if (!r) return "ไรเดอร์";
      return (`${r.natId_firstName || ""} ${r.natId_lastName || ""}`).trim() || r.username || "ไรเดอร์";
    },

    reviewableItems() {
      return (this.order?.items || []).filter(i => !i.productReview);
    },

    hasSellerDone() { return !!this.order?.sellerReview; },
    hasRiderDone() { return !!this.order?.riderReview; },
    hasProductsDone() {
      return this.reviewableItems.length === 0;
    },

    availableTabs() {
      const tabs = [
        { key: "seller", label: "ร้านค้า", icon: "mdi-store-outline", done: this.hasSellerDone },
        { key: "products", label: "สินค้า", icon: "mdi-package-variant-outline", done: this.hasProductsDone },
      ];
      if (this.order?.riderId) {
        tabs.splice(1, 0, { key: "rider", label: "ไรเดอร์", icon: "mdi-motorbike", done: this.hasRiderDone });
      }
      return tabs;
    },

    canSubmit() {
      if (this.activeTab === "seller") return this.sellerRating > 0 && !this.hasSellerDone;
      if (this.activeTab === "rider") return this.riderRating > 0 && !this.hasRiderDone;
      if (this.activeTab === "products") return this.reviewableItems.some(i => (this.productRatings[i.id] || 0) > 0);
      return false;
    },
  },

  watch: {
    value(v) {
      if (v) this.reset();
    },
    order() {
      this.initProductRatings();
      this.setDefaultTab();
    },
  },

  methods: {
    getProductImage,

    reset() {
      this.sellerRating = 0;
      this.sellerComment = "";
      this.riderRating = 0;
      this.riderComment = "";
      this.initProductRatings();
      this.setDefaultTab();
    },

    initProductRatings() {
      const ratings = {};
      const comments = {};
      const hovers = {};
      (this.order?.items || []).forEach(i => {
        ratings[i.id] = 0;
        comments[i.id] = "";
        hovers[i.id] = 0;
      });
      this.productRatings = ratings;
      this.productComments = comments;
      this.productHovers = hovers;
    },

    setDefaultTab() {
      if (!this.hasSellerDone) { this.activeTab = "seller"; return; }
      if (this.order?.riderId && !this.hasRiderDone) { this.activeTab = "rider"; return; }
      if (!this.hasProductsDone) { this.activeTab = "products"; return; }
      this.activeTab = "seller";
    },

    close() {
      this.dialog = false;
    },

    async submit() {
      if (!this.order || this.saving) return;
      this.saving = true;
      try {
        if (this.activeTab === "seller" && this.sellerRating > 0) {
          await createSellerReviewApi({ orderId: this.order.id, rating: this.sellerRating, comment: this.sellerComment || undefined });
          await swalAlert.Success("รีวิวร้านค้าสำเร็จ", "ขอบคุณสำหรับความคิดเห็นของคุณ");
          this.$emit("reviewed");
          this.close();
        } else if (this.activeTab === "rider" && this.riderRating > 0) {
          await createRiderReviewApi({ orderId: this.order.id, rating: this.riderRating, comment: this.riderComment || undefined });
          await swalAlert.Success("รีวิวไรเดอร์สำเร็จ", "ขอบคุณสำหรับความคิดเห็นของคุณ");
          this.$emit("reviewed");
          this.close();
        } else if (this.activeTab === "products") {
          const toReview = this.reviewableItems.filter(i => (this.productRatings[i.id] || 0) > 0);
          await Promise.all(
            toReview.map(i =>
              createProductReviewApi({
                orderItemId: i.id,
                rating: this.productRatings[i.id],
                comment: this.productComments[i.id] || undefined,
              })
            )
          );
          await swalAlert.Success("รีวิวสินค้าสำเร็จ", `รีวิว ${toReview.length} รายการเรียบร้อย`);
          this.$emit("reviewed");
          this.close();
        }
      } catch (e) {
        swalAlert.Error("ส่งรีวิวไม่สำเร็จ", e?.response?.data?.message || "กรุณาลองใหม่อีกครั้ง");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.review-card { background: #fff; }

.review-icon-wrap {
  width: 40px; height: 40px;
  background: linear-gradient(135deg, #F4811F, #e06000);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.review-title { font-size: 15px; font-weight: 800; color: #0f172a; }
.review-sub { font-size: 11px; color: #94a3b8; }

.review-tabs {
  display: flex; gap: 6px; flex-wrap: wrap;
}

.rtab {
  padding: 6px 14px;
  font-size: 12px; font-weight: 600;
  border-radius: 999px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  display: flex; align-items: center;
}
.rtab:hover { border-color: #F4811F; color: #F4811F; }
.rtab--active { border-color: #F4811F; background: #fff5ed; color: #F4811F; }
.rtab--done { border-color: #10b981; background: #f0fdf4; color: #10b981; }
.rtab--done.rtab--active { border-color: #10b981; background: #dcfce7; }

.review-body { min-height: 200px; }

.section-label {
  font-size: 13px; font-weight: 700; color: #374151;
  margin-bottom: 14px;
}

.shop-info { }
.info-name { font-size: 14px; font-weight: 700; color: #0f172a; }
.info-sub { font-size: 11px; color: #94a3b8; }

.product-review-name {
  font-size: 13px; font-weight: 600; color: #1e293b;
  line-height: 1.4;
}
</style>
